/**
 * LightReach Battery Price Caps modal (React dashboard).
 * Mirrors Angular `PriceCapsLightreachComponent` modal behavior using the same Contract API endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Table as AntTable, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import DashboardModal from './DashboardModal';
import type { LightreachBatteryCapModalProps } from './LightreachBatteryCapModal.types';

function fmtUsdCell(n: unknown): string {
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(x);
}

function normalizeCapsRows(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) return raw as Record<string, unknown>[];
    if (raw && typeof raw === 'object') {
        const o = raw as any;
        if (Array.isArray(o.Data)) return o.Data as Record<string, unknown>[];
        if (Array.isArray(o.data)) return o.data as Record<string, unknown>[];
    }
    return [];
}

export const LightreachBatteryCapModal: React.FC<LightreachBatteryCapModalProps> = ({ open, onClose, theme, leadId }) => {
    const [rows, setRows] = useState<Record<string, unknown>[]>([]);
    const [loading, setLoading] = useState(false);
    const [noticeApi, noticeContextHolder] = notification.useNotification({ maxCount: 3 });

    const columns: ColumnsType<Record<string, unknown>> = useMemo(
        () => [
            {
                title: 'Product Id',
                key: 'Id',
                width: 220,
                render: (_, r) => String((r as any).Id ?? (r as any).id ?? '—'),
                sorter: (a, b) =>
                    String((a as any).Id ?? (a as any).id ?? '').localeCompare(String((b as any).Id ?? (b as any).id ?? ''), undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    }),
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Manufacturer',
                key: 'manufacturer',
                width: 160,
                render: (_, r) => String((r as any).manufacturer ?? (r as any).Manufacturer ?? '—'),
                sorter: (a, b) =>
                    String((a as any).manufacturer ?? (a as any).Manufacturer ?? '').localeCompare(
                        String((b as any).manufacturer ?? (b as any).Manufacturer ?? ''),
                        undefined,
                        { numeric: true, sensitivity: 'base' },
                    ),
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Model',
                key: 'model',
                ellipsis: true,
                render: (_, r) => String((r as any).model ?? (r as any).Model ?? '—'),
                sorter: (a, b) =>
                    String((a as any).model ?? (a as any).Model ?? '').localeCompare(String((b as any).model ?? (b as any).Model ?? ''), undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    }),
                sortDirections: ['ascend', 'descend'],
            },
            {
                title: 'Max Price',
                key: 'maxPrice',
                width: 140,
                render: (_, r) => fmtUsdCell((r as any).maxPrice ?? (r as any).MaxPrice ?? (r as any).max_price),
                sorter: (a, b) => {
                    const na = Number((a as any).maxPrice ?? (a as any).MaxPrice ?? (a as any).max_price);
                    const nb = Number((b as any).maxPrice ?? (b as any).MaxPrice ?? (b as any).max_price);
                    const va = Number.isFinite(na) ? na : 0;
                    const vb = Number.isFinite(nb) ? nb : 0;
                    return va - vb;
                },
                sortDirections: ['ascend', 'descend'],
            },
        ],
        [],
    );

    useEffect(() => {
        let cancelled = false;

        async function run() {
            if (!open) return;
            if (leadId == null || String(leadId).trim() === '') {
                setRows([]);
                noticeApi.warning({
                    message: 'LightReach lead ID required',
                    description: 'Complete LightReach lead sync so a finance partner lead ID is available.',
                    className: `obd-toast obd-toast--${theme}`,
                });
                return;
            }

            setLoading(true);
            setRows([]);
            try {
                const res = await quoteServices.getPriceCaps(leadId);
                const normalized = normalizeCapsRows(res);
                if (!cancelled) setRows(normalized);
                if (normalized.length === 0) {
                    noticeApi.info({
                        message: 'No price caps',
                        description: 'No battery price caps were returned for this LightReach lead.',
                        className: `obd-toast obd-toast--${theme}`,
                    });
                }
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Request failed.';
                if (!cancelled) onClose();
                noticeApi.error({
                    message: 'Could not load LightReach price caps',
                    description: msg,
                    className: `obd-toast obd-toast--${theme}`,
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        void run();
        return () => {
            cancelled = true;
        };
    }, [open, leadId, noticeApi, onClose, theme]);

    return (
        <>
            {noticeContextHolder}
            <DashboardModal
                title="Price Caps"
                open={open}
                onClose={onClose}
                footer={null}
                width="min(1100px, 98vw)"
                theme={theme}
                maskClosable={false}
                destroyOnClose
                className="obd-dashboard-modal--lr-price-caps"
                rootClassName={`obd-theme-${theme}`}
                zIndex={10070}
                loading={loading}
                loadingMessage="Loading price caps..."
            >
                <div className="obd-lr-price-caps-table">
                    <AntTable<Record<string, unknown>>
                        size="middle"
                        loading={false}
                        columns={columns}
                        dataSource={rows}
                        rowKey={(r, i) => String((r as any).Id ?? (r as any).id ?? `lr-cap-${i}`)}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) => (total > 0 ? `${range[0]}–${range[1]} of ${total}` : ''),
                            className: 'obd-lr-price-caps-pagination',
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </DashboardModal>
        </>
    );
};

export default LightreachBatteryCapModal;

